import { CreateBinaryOptions, isUUID, MedplumClient, MedplumRequestOptions } from "@medplum/core";
import { Attachment, DocumentReference } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import {
  AllowedFileTypes,
  MAX_FILE_SIZE,
  MAX_HASHED_FILE_SIZE,
  MIME_TYPE_TO_FILE_EXTENSION,
} from "@/types/uploads";

import { joinReferenceString } from "./medplum";
import { showSystemErrorNotification } from "./notifications";

export const isFileTypeAllowed = (file: File) =>
  Object.values(AllowedFileTypes).includes(file.type as AllowedFileTypes);

export const isFileSizeAllowed = (file: File) => file.size < MAX_FILE_SIZE;

/**
 * Computes the SHA-1 digest of file content, base64-encoded — matching the
 * FHIR `Attachment.hash` field definition ("the calculated hash of the data
 * using SHA-1... represented using base64"). Used to detect corruption or
 * unauthorized modification of stored attachment content: set on write via
 * createAttachmentWithHash, checked on read via getFileBlob. jsdom's Blob
 * doesn't implement `arrayBuffer()` itself — see vitest.setup.ts for the
 * test-environment polyfill; real browsers support this natively.
 */
export async function computeAttachmentHash(data: Blob): Promise<string> {
  const buffer = await data.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-1", buffer);
  const bytes = new Uint8Array(digest);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/**
 * Wraps medplum.createAttachment to also populate Attachment.hash. Medplum's
 * SDK uploads the Binary but never sets this field itself, so without this
 * wrapper every attachment created through this app has no integrity
 * checksum at all. Hashing is best-effort: a failure here (e.g. an
 * unsupported browser) is reported but never blocks the upload, since the
 * upload succeeding matters more than the checksum existing. Files over
 * MAX_HASHED_FILE_SIZE are uploaded without a hash.
 */
export async function createAttachmentWithHash(
  medplum: MedplumClient,
  input: CreateBinaryOptions,
  requestOptions?: MedplumRequestOptions,
): Promise<Attachment> {
  const attachment = await medplum.createAttachment(input, requestOptions);

  if (!(input.data instanceof Blob) || input.data.size > MAX_HASHED_FILE_SIZE) return attachment;

  try {
    attachment.hash = await computeAttachmentHash(input.data);
  } catch (error) {
    Sentry.captureException(error, { extra: { context: "createAttachmentWithHash" } });
  }

  return attachment;
}

// The complete PNG signature, and the JPEG start-of-image marker.
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
const LONGEST_IMAGE_SIGNATURE_LENGTH = PNG_SIGNATURE.length;

/**
 * Reads the leading bytes of a blob. Goes through `FileReader` because jsdom, the unit suite's DOM
 * implementation, does not provide `Blob.arrayBuffer()`.
 * @param {Blob} blob - The blob to read from.
 * @param {number} byteCount - How many bytes to read from the start.
 * @returns {Promise<Uint8Array>} The leading bytes, shorter than requested for a smaller blob.
 */
function readLeadingBytes(blob: Blob, byteCount: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob.slice(0, byteCount));
  });
}

/**
 * Identifies a file as a PNG or JPEG from its leading bytes, and returns that format's canonical
 * media type. `File.type` comes from the browser's filename guess, so it can disagree with the
 * content — a PNG saved as `logo.jpg` is reported as `image/jpeg`. The detected type is what should
 * be stored on an Attachment, so the recorded media type matches the bytes behind it.
 * @param {File} file - The candidate image file.
 * @returns {Promise<AllowedFileTypes | undefined>} The detected media type, or undefined when the
 * content is neither a PNG nor a JPEG.
 */
export async function detectImageContentType(file: File): Promise<AllowedFileTypes | undefined> {
  const header = await readLeadingBytes(file, LONGEST_IMAGE_SIGNATURE_LENGTH);
  const matches = (signature: number[]) => signature.every((byte, index) => header[index] === byte);

  if (matches(PNG_SIGNATURE)) return AllowedFileTypes.PNG;
  if (matches(JPEG_SIGNATURE)) return AllowedFileTypes.JPEG;

  return undefined;
}

/**
 * Checks whether the browser can decode a blob as an image using `createImageBitmap`. This is a
 * second pass beyond the header check: it rejects most corrupt or truncated bodies behind a valid
 * signature, but browsers may still decode some truncated JPEGs without error. If the browser
 * does not implement `createImageBitmap`, returns true; the caller's header check is the only guard.
 * @param {Blob} blob - The candidate image.
 * @returns {Promise<boolean>} True when the browser can decode the image or cannot perform decode
 * detection, false when decoding fails.
 */
export async function canDecodeImage(blob: Blob): Promise<boolean> {
  if (typeof createImageBitmap !== "function") {
    return true;
  }

  try {
    const bitmap = await createImageBitmap(blob);
    bitmap.close();
    return true;
  } catch {
    return false;
  }
}

interface CreateFileAttachmentOptions {
  /** Media type to record instead of the file's declared one, such as a type detected from content. */
  contentType?: string;
  /** Request options forwarded to Medplum, such as an AbortSignal. */
  request?: MedplumRequestOptions;
}

/**
 * Uploads a file to Medplum as an Attachment, deriving the filename from the file itself and
 * populating Attachment.hash via createAttachmentWithHash. A shared shape for attachment uploads.
 * @param {MedplumClient} medplum - Authenticated Medplum client.
 * @param {File} file - The file to upload.
 * @param {CreateFileAttachmentOptions} [options] - Content type override and Medplum request options.
 * @returns {Promise<Attachment>} The created Attachment.
 */
export async function createFileAttachment(
  medplum: MedplumClient,
  file: File,
  options?: CreateFileAttachmentOptions,
): Promise<Attachment> {
  return await createAttachmentWithHash(
    medplum,
    { data: file, contentType: options?.contentType ?? file.type, filename: file.name },
    options?.request,
  );
}

/**
 * What getFileBlob needs to check a download against its stored Attachment.hash. `ownerReference`
 * names the resource that holds the attachment (e.g. `DocumentReference/<id>`), so an integrity
 * alert points at that resource and never at the underlying Binary.
 */
export interface AttachmentVerification {
  hash: string | undefined;
  ownerReference: string | undefined;
}

export async function getFileBlob(
  medplum: MedplumClient,
  name: string,
  url: string | undefined,
  verification?: AttachmentVerification,
): Promise<Blob> {
  if (!url) {
    showSystemErrorNotification();
    throw new Error(`No URL available for file: ${name}`);
  }

  const attachmentId = getBinaryIdFromAttachmentUrl(url);

  if (!attachmentId) {
    showSystemErrorNotification();
    throw new Error(`No attachment ID found for file: ${name}`);
  }

  const blob = await medplum.download(joinReferenceString("Binary", attachmentId));

  if (verification?.hash && blob.size <= MAX_HASHED_FILE_SIZE) {
    await verifyAttachmentHash(blob, verification.hash, verification.ownerReference);
  }

  return blob;
}

/**
 * Compares downloaded content against its expected FHIR Attachment.hash and
 * alerts (does not throw) on mismatch — this is the read-side half of the
 * integrity check established at upload time by createAttachmentWithHash.
 * Never blocks viewing/downloading: a corrupted file should still be
 * shown/handled by the caller, just flagged for follow-up. Only the owning
 * resource's reference is reported, never file content, name, or Binary ID.
 */
async function verifyAttachmentHash(
  blob: Blob,
  expectedHash: string,
  ownerReference: string | undefined,
): Promise<void> {
  try {
    const actualHash = await computeAttachmentHash(blob);
    if (actualHash !== expectedHash) {
      Sentry.captureMessage("Attachment content hash mismatch", {
        level: "error",
        extra: { ownerReference },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { context: "verifyAttachmentHash", ownerReference } });
  }
}

export function getFileExtensionFromMimeType(mimeType: string): string {
  return MIME_TYPE_TO_FILE_EXTENSION[mimeType.toLowerCase() as AllowedFileTypes] || "";
}

/**
 * Appends the extension implied by `mimeType` unless `filename` already ends with it.
 *
 * @param filename - The file name, with or without an extension.
 * @param mimeType - The file's MIME type; without a known extension the name is returned as is.
 * @returns The file name carrying exactly one trailing extension for the MIME type.
 */
export function ensureFileExtension(filename: string, mimeType?: string): string {
  if (!mimeType) return filename;

  const extension = getFileExtensionFromMimeType(mimeType);
  if (!extension) return filename;

  const acceptedExtensions = extension === ".jpg" ? [".jpg", ".jpeg"] : [extension];
  const lowerCaseFilename = filename.toLowerCase();
  if (acceptedExtensions.some((accepted) => lowerCaseFilename.endsWith(accepted))) {
    return filename;
  }
  return filename + extension;
}

export function getAttachmentTitle(attachment: Attachment): string {
  if (attachment.title) return attachment.title;
  const url = attachment.url;
  if (!url) return "Uploaded file";
  try {
    const path = url.split("/").pop();
    const segment = path?.split("?")[0];
    return segment ?? "Uploaded file";
  } catch {
    return "Uploaded file";
  }
}

export function getBinaryIdFromAttachmentUrl(url: string | undefined): string | undefined {
  if (!url) return;

  if (url.startsWith("Binary/")) {
    return url.split("/").at(-1);
  }

  // Medplum storage service does not allow CORS requests for security reasons.
  // So instead, we have to use the FHIR Binary API to fetch the source code.
  // Example: https://storage.staging.medplum.com/binary/272a11dc-5b01-4c05-a14e-5bf53117e1e9/69303e8d-36f2-4417-b09b-60c15f221b09?Expires=...
  // The Binary ID is the first UUID in the URL.
  try {
    return new URL(url).pathname.split("/").find(isUUID);
  } catch {
    return undefined;
  }
}

export async function deleteFile(medplum: MedplumClient, file: DocumentReference) {
  try {
    const attachmentUrl = file.content?.[0]?.attachment?.url;
    if (attachmentUrl) {
      const attachmentId = getBinaryIdFromAttachmentUrl(attachmentUrl);
      if (attachmentId) {
        await medplum.deleteResource("Binary", attachmentId);
      }
    }
  } catch (error) {
    console.error(`Binary not found for file: "${file.id}".`);
  } finally {
    if (file.id) {
      // Delete DocumentReference anyway since the linked Binary was already deleted,
      // even if the try/catch block fails
      await medplum.deleteResource("DocumentReference", file.id);
    }
  }
}
